/** A Chapter groups and orders ChapterSections for the reader-facing table of contents. */
export interface Chapter {
  id: string
  title: string
  chapterSections: string[]
  /** Author-only working note about the chapter. Never sent to readers. */
  note?: string
}

/** Extract a flat list of all ChapterSection keys from an ordered Chapter array. */
export function flattenChaptersToSectionKeys(chapters: Chapter[]): string[] {
  const out: string[] = []
  for (const ch of chapters) {
    for (const key of ch.chapterSections ?? []) {
      out.push(key)
    }
  }
  return out
}

/**
 * Merge persisted chapters with a canonical set of section keys.
 * - Removes stale keys (no longer present in canonical) from each chapter.
 * - Returns unattached keys (present in canonical but not in any chapter) separately.
 */
export function mergeChaptersWithCanonicalKeys(
  saved: Chapter[],
  canonicalKeys: string[],
): { chapters: Chapter[]; unattached: string[] } {
  const canonicalSet = new Set(canonicalKeys)
  const assignedKeys = new Set<string>()

  const chapters = saved.map((ch) => {
    const filtered = (ch.chapterSections ?? []).filter((k) => {
      if (!canonicalSet.has(k)) {
        return false
      }
      assignedKeys.add(k)
      return true
    })
    return { ...ch, chapterSections: filtered }
  })

  const unattached = canonicalKeys.filter((k) => !assignedKeys.has(k))

  return { chapters, unattached }
}

export function generateChapterId(): string {
  const hex = () => Math.random().toString(16).slice(2, 10)
  return `${hex()}${hex()}`
}

/** Stable id for a `^` section marker: `filename:headingBlockId` (matches editor dedupe keys). */
export function chapterSectionKey(filename: string, headingBlockId: string): string {
  return `${filename}:${headingBlockId}`
}

type ContentFileForSectionOrder = { filename: string; order: number }

/** Compute canonical section keys from server data (chapter order x doc order). */
export function canonicalChapterSectionKeysFromServer(
  chapters: ContentFileForSectionOrder[],
  chapterSectionsFromServer: Array<{ filename: string; headingBlockId: string }> | null | undefined,
): string[] {
  if (!chapterSectionsFromServer?.length) {
    return []
  }
  const ordered = [...chapters].sort((a, b) => a.order - b.order)
  const chapterByFilename = new Map(ordered.map((c) => [c.filename, c]))
  const byFile = new Map<string, Array<{ headingBlockId: string }>>()
  for (const row of chapterSectionsFromServer) {
    const sourceChapter = chapterByFilename.get(row.filename)
    if (!sourceChapter) {
      continue
    }
    const list = byFile.get(row.filename) ?? []
    list.push({ headingBlockId: row.headingBlockId })
    byFile.set(row.filename, list)
  }
  const out: string[] = []
  const seen = new Set<string>()
  for (const ch of ordered) {
    const rows = byFile.get(ch.filename)
    if (!rows?.length) {
      continue
    }
    for (const r of rows) {
      const k = chapterSectionKey(ch.filename, r.headingBlockId)
      if (seen.has(k)) {
        continue
      }
      seen.add(k)
      out.push(k)
    }
  }
  return out
}
